import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Chip,
  TextField,
  Grid,
  InputAdornment,
  IconButton,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { formatCurrency } from '../../utils/formatters';
import { API_URL } from '../../utils/config';

interface Bill {
  id: string;
  transactionNumber: string;
  transactionDate: string;
  dueDate: string;
  vendorName: string;
  totalAmount: number;
  balanceDue: number;
  status: string;
  currencyCode: string;
}

interface BillsData {
  bills: Bill[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export const BillsList: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [bills, setBills] = useState<Bill[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use refs to track fetch state - Pattern 1 from react-patterns.md
  const hasFetchedRef = useRef(false);
  const lastFetchParamsRef = useRef<string>('');
  
  // Calculate summary statistics
  const totalBills = totalCount;
  const totalOutstanding = bills.reduce((sum, bill) => sum + bill.balanceDue, 0);
  const overdueCount = bills.filter(bill => 
    new Date(bill.dueDate) < new Date() && bill.balanceDue > 0
  ).length;

  useEffect(() => {
    // Build params string to check if we need to refetch
    const currentParams = `${page}-${rowsPerPage}-${statusFilter}-${dateFrom}-${dateTo}`;
    
    // Check if params changed or if this is first fetch
    if (lastFetchParamsRef.current !== currentParams || !hasFetchedRef.current) {
      lastFetchParamsRef.current = currentParams;
      hasFetchedRef.current = false;
    }
    
    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const fetchBills = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const params = new URLSearchParams({
          limit: rowsPerPage.toString(),
          offset: (page * rowsPerPage).toString(),
        });
        
        if (statusFilter) params.append('status', statusFilter);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);

        const response = await authenticatedFetch(`${API_URL}/api/accounting/user/bills?${params}`);
        if (response && response.ok) {
          const data: { success: boolean; data: BillsData } = await response.json();
          if (data.success) {
            setBills(data.data.bills);
            setTotalCount(data.data.pagination.total);
          }
        } else {
          setError('Failed to load bills');
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setError('Failed to load bills. Please try again later.');
          console.error('Error fetching bills:', err);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBills();
  }, [page, rowsPerPage, statusFilter, dateFrom, dateTo, authenticatedFetch]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (billId: string) => {
    navigate(`/accounting/bills/${billId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'open':
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const isOverdue = (dueDate: string, balanceDue: number) => {
    return new Date(dueDate) < new Date() && balanceDue > 0;
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
  };

  // Filter bills locally by search term
  const filteredBills = bills.filter(bill => 
    searchTerm === '' || 
    bill.transactionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bill.vendorName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <Container
        maxWidth="xl"
        sx={{ py: 4, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/accounting/vendor')}
        >
          Subsidiary Accounting
        </Link>
        <Typography color="text.primary">Bills</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Bills
        </Typography>
        
        {/* Summary Statistics */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Bills
                </Typography>
                <Typography variant="h5">
                  {totalBills}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Outstanding
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(totalOutstanding)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Overdue
                </Typography>
                <Typography variant="h5" color="error">
                  {overdueCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Transaction number or vendor..."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton onClick={handleClearFilters} title="Clear filters">
                  <ClearIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction #</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Transaction Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell align="right">Balance Due</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No bills found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBills.map((bill) => (
                  <TableRow 
                    key={bill.id}
                    hover
                    onClick={() => handleRowClick(bill.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{bill.transactionNumber}</TableCell>
                    <TableCell>{bill.vendorName}</TableCell>
                    <TableCell>
                      {new Date(bill.transactionDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ color: isOverdue(bill.dueDate, bill.balanceDue) ? 'error.main' : 'inherit' }}>
                      {new Date(bill.dueDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(bill.totalAmount, bill.currencyCode)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(bill.balanceDue, bill.currencyCode)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={bill.status} 
                        size="small"
                        color={getStatusColor(bill.status) as any}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Container>
  );
};